import React from "react";
import { Link } from "react-router-dom";
import { APP_NAME, SUPPORT_EMAIL } from "../constants/expiry";
import { SentrilogIcon } from "../components/Icons";

const EFFECTIVE_DATE = "March 1, 2026";

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-[#111827] mb-4">{title}</h2>
    <div className="text-[#374151] text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const PrivacyPolicy: React.FC = () => (
  <div className="min-h-screen bg-[#F9FAFB]">
    {/* Nav */}
    <header className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <div className="w-8 h-8 bg-[#2563EB] rounded-lg flex items-center justify-center">
            <SentrilogIcon className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-[#111827] text-lg">{APP_NAME}</span>
        </Link>
        <Link
          to="/"
          className="text-sm text-[#6B7280] hover:text-[#111827] transition-colors"
        >
          ← Back to home
        </Link>
      </div>
    </header>

    {/* Content */}
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-3xl font-extrabold text-[#111827] mb-2">
          Privacy Policy
        </h1>
        <p className="text-sm text-[#6B7280]">
          Effective date: {EFFECTIVE_DATE}
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-[#E5E7EB] px-8 py-10">
        <p className="text-[#374151] text-sm leading-relaxed mb-8">
          Welcome to {APP_NAME}. We take your privacy seriously. This Privacy
          Policy explains how {APP_NAME} ("<strong>we</strong>", "
          <strong>our</strong>", or "<strong>us</strong>") collects, uses,
          discloses, and safeguards your information when you use our platform
          at sentrilog.com and related services (collectively, the "
          <strong>Service</strong>"). Please read this policy carefully. If you
          disagree with its terms, please discontinue use of the Service.
        </p>

        <Section title="1. Information We Collect">
          <p>We collect information you provide directly to us, including:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>Account data</strong> — name, email address, company name,
              and password when you register.
            </li>
            <li>
              <strong>Profile data</strong> — job title, phone number, and
              preferences you add to your profile.
            </li>
            <li>
              <strong>Content data</strong> — documents, expiry dates, notes,
              and other data you upload or enter into the Service.
            </li>
            <li>
              <strong>Communications</strong> — messages you send us via email
              or the in-app support chat.
            </li>
            <li>
              <strong>Payment data</strong> — billing address and payment method
              details processed securely by our payment provider (Stripe). We do
              not store raw card numbers.
            </li>
          </ul>
          <p className="mt-3">
            We also collect data automatically when you use the Service:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>Usage data</strong> — pages visited, features used, and
              actions taken within the platform.
            </li>
            <li>
              <strong>Device &amp; log data</strong> — IP address, browser type,
              operating system, and referring URLs.
            </li>
            <li>
              <strong>Cookies &amp; similar technologies</strong> — see our{" "}
              <Link to="/cookie-policy" className="text-[#2563EB] underline">
                Cookie Policy
              </Link>{" "}
              for details.
            </li>
          </ul>
        </Section>

        <Section title="2. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Provide, operate, and improve the Service.</li>
            <li>
              Send transactional emails such as expiry reminders, magic links,
              and team invitations — these are core to the Service.
            </li>
            <li>
              Respond to your inquiries and provide customer support.
            </li>
            <li>
              Send product updates and marketing communications (you may
              opt out at any time).
            </li>
            <li>
              Monitor usage patterns to detect and prevent abuse or security
              incidents.
            </li>
            <li>
              Comply with legal obligations and enforce our Terms of Service.
            </li>
            <li>
              Process payments and manage subscriptions.
            </li>
          </ul>
          <p className="mt-3">
            We process your data on the following legal bases: performance of a
            contract (delivering the Service), legitimate interests (security,
            fraud prevention, product improvement), your consent (marketing
            emails), and compliance with legal obligations.
          </p>
        </Section>

        <Section title="3. How We Share Your Information">
          <p>
            We do <strong>not</strong> sell your personal data. We may share
            information in the following limited circumstances:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>Service providers</strong> — third parties that help us
              operate the Service (e.g., cloud hosting, email delivery, payment
              processing, analytics). They are bound by confidentiality
              agreements and may only use your data to provide services to us.
            </li>
            <li>
              <strong>Team members</strong> — if you are part of a workspace,
              other workspace members with appropriate roles can view content
              you have added to that workspace.
            </li>
            <li>
              <strong>Legal requirements</strong> — if required by law, court
              order, or governmental authority.
            </li>
            <li>
              <strong>Business transfers</strong> — in connection with a merger,
              acquisition, or sale of assets, your data may be transferred. We
              will notify you before your data is subject to a different privacy
              policy.
            </li>
          </ul>
        </Section>

        <Section title="4. Data Retention">
          <p>
            We retain your personal data for as long as your account is active
            or as needed to provide the Service. If you close your account, we
            will delete or anonymise your personal data within 90 days, except
            where we are required to retain it for legal, tax, or audit purposes.
          </p>
          <p>
            Document content you have uploaded is deleted within 30 days of
            account closure.
          </p>
        </Section>

        <Section title="5. Data Security">
          <p>
            We implement industry-standard technical and organisational measures
            to protect your data, including:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Encryption of data in transit (TLS 1.2+) and at rest (AES-256).</li>
            <li>Role-based access control limiting who can access your data.</li>
            <li>Regular security reviews and penetration testing.</li>
            <li>Secure password hashing (bcrypt).</li>
          </ul>
          <p className="mt-3">
            No method of transmission over the internet is 100% secure. If you
            believe your account has been compromised, please contact us
            immediately at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-[#2563EB] underline"
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section title="6. Your Rights">
          <p>
            Depending on your location, you may have the following rights
            regarding your personal data:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              <strong>Access</strong> — request a copy of the data we hold about
              you.
            </li>
            <li>
              <strong>Rectification</strong> — request correction of inaccurate
              data.
            </li>
            <li>
              <strong>Erasure</strong> — request deletion of your data (subject
              to legal retention requirements).
            </li>
            <li>
              <strong>Portability</strong> — receive your data in a
              machine-readable format.
            </li>
            <li>
              <strong>Objection / restriction</strong> — object to or restrict
              certain processing activities.
            </li>
            <li>
              <strong>Withdraw consent</strong> — opt out of marketing emails
              via the unsubscribe link in any email.
            </li>
          </ul>
          <p className="mt-3">
            To exercise any of these rights, please email us at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-[#2563EB] underline"
            >
              {SUPPORT_EMAIL}
            </a>
            . We will respond within 30 days.
          </p>
        </Section>

        <Section title="7. International Data Transfers">
          <p>
            {APP_NAME} is operated from the European Union. If you access the
            Service from outside the EU, your data may be transferred to and
            processed in countries with different data protection laws. Where
            required, we rely on Standard Contractual Clauses (SCCs) approved by
            the European Commission to safeguard such transfers.
          </p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>
            The Service is not directed to individuals under the age of 16. We
            do not knowingly collect personal data from children. If you believe
            a child has provided us with personal information, please contact us
            and we will promptly delete it.
          </p>
        </Section>

        <Section title="9. Third-Party Links">
          <p>
            The Service may contain links to third-party websites. We are not
            responsible for the privacy practices of those sites and encourage
            you to review their privacy policies before providing any personal
            data.
          </p>
        </Section>

        <Section title="10. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will revise the effective date at the top of this page and, for
            material changes, notify you by email or via an in-app notice. Your
            continued use of the Service after any change constitutes your
            acceptance of the updated policy.
          </p>
        </Section>

        <Section title="11. Contact Us">
          <p>
            If you have any questions or concerns about this Privacy Policy,
            please contact our Data Privacy team at:
          </p>
          <div className="mt-3 p-4 bg-[#F9FAFB] rounded-xl border border-[#E5E7EB] text-sm">
            <p className="font-semibold text-[#111827]">{APP_NAME}</p>
            <p>
              Email:{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-[#2563EB] underline"
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
            <p>Website: sentrilog.com</p>
          </div>
        </Section>
      </div>

      {/* Bottom links */}
      <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-[#6B7280]">
        <Link to="/terms" className="hover:text-[#111827] transition-colors underline">
          Terms of Service
        </Link>
        <Link
          to="/cookie-policy"
          className="hover:text-[#111827] transition-colors underline"
        >
          Cookie Policy
        </Link>
        <Link to="/" className="hover:text-[#111827] transition-colors">
          ← Back to home
        </Link>
      </div>
    </main>
  </div>
);

export default PrivacyPolicy;
